// Localization helper - matches Angular localization.helper.js implementation
// Provides runtime locale detection and string lookup with fallback to default locale

import { parsePropertiesFile } from "./propertiesParser";
import defaultLocaleData from "../locales/default/locale.json";
import zhCnLocaleData from "../locales/zh_cn/locale.json";

// Import properties files as text using webpack raw-loader or fetch
// For CRA, we'll embed them as modules
import defaultStringsFile from "../locales/default/strings.properties.js";
import zhCnStringsFile from "../locales/zh_cn/strings.properties.js";

// Parse properties files at module load time
const defaultStrings = parsePropertiesFile(defaultStringsFile);
const zhCnStrings = parsePropertiesFile(zhCnStringsFile);

// Build locale data structure matching Angular pattern
const localeData = {
  default: {
    country: defaultLocaleData.country,
    language: defaultLocaleData.language,
    strings: defaultStrings,
  },
  zh_cn: {
    country: zhCnLocaleData.country,
    language: zhCnLocaleData.language,
    strings: zhCnStrings,
  },
};

/**
 * Get cookie value by name
 * @param {string} name - Cookie name
 * @returns {string|null} - Cookie value or null
 */
function getCookie(name) {
  if (typeof document === "undefined") return null;

  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop().split(";").shift();
    console.log(`[Localization] getCookie('${name}') = '${cookieValue}'`);
    return cookieValue;
  }
  console.log(`[Localization] getCookie('${name}') = null (not found)`);
  console.log(`[Localization] All cookies: ${document.cookie}`);
  return null;
}

/**
 * Get current locale language from cookies
 * @returns {string} - Language code (e.g., 'en', 'zh')
 */
function getLocaleLanguage() {
  const lang = getCookie("CK_LANG_CODE") || "en";
  console.log(`[Localization] getLocaleLanguage() returning: '${lang}'`);
  return lang;
}

/**
 * Get current locale country from cookies
 * @returns {string} - Country code (e.g., 'us', 'cn')
 */
function getLocaleCountry() {
  const country = getCookie("CK_ISO_CODE") || "us";
  console.log(`[Localization] getLocaleCountry() returning: '${country}'`);
  return country;
}

// Initialize current locale - matches Angular logic exactly
const currentLanguage = getLocaleLanguage();
const currentCountry = getLocaleCountry();

console.log(`[Localization] Initializing with cookies: language=${currentLanguage}, country=${currentCountry}`);
console.log(`[Localization] Available locales:`, Object.keys(localeData));

// Start with default locale
let currentLocaleData = {
  country: localeData.default.country,
  language: localeData.default.language,
  strings: { ...localeData.default.strings }, // Copy default strings
};

let currentLocaleKey = "default";

// Check if a non-default locale matches current country/language
Object.keys(localeData).forEach((localeKey) => {
  if (localeKey !== "default") {
    const data = localeData[localeKey];
    console.log(`[Localization] Checking locale ${localeKey}: country=${data.country}, language=${data.language}`);
    if (data.country === currentCountry && data.language === currentLanguage) {
      console.log(`[Localization] ✅ Matched locale: ${localeKey}`);
      currentLocaleData.country = data.country;
      currentLocaleData.language = data.language;

      // Extend default strings with locale-specific strings (override pattern)
      // This ensures missing translations fall back to default
      Object.assign(currentLocaleData.strings, data.strings);

      currentLocaleKey = localeKey;
    }
  }
});

console.log(
  `[Localization] Active locale: ${currentLocaleKey} (country: ${currentCountry}, language: ${currentLanguage})`
);
console.log(`[Localization] Total strings loaded: ${Object.keys(currentLocaleData.strings).length}`);
console.log(`[Localization] Sample strings:`, {
  'quiz.start': currentLocaleData.strings['quiz.start'],
  'quiz.title': currentLocaleData.strings['quiz.title'],
  'results.title': currentLocaleData.strings['results.title']
});

/**
 * Get the current locale key
 * @returns {string} - Locale key (e.g., 'default', 'zh_cn')
 */
export function getLocaleKey() {
  return currentLocaleKey;
}

/**
 * Get all strings for current locale
 * @returns {Object} - All strings as key-value pairs
 */
export function getAllStrings() {
  return currentLocaleData.strings;
}

/**
 * Get a localized string by key
 * Matches Angular implementation: returns [KEY_NAME] if not found for easy debugging
 * @param {string} key - String key (e.g., 'quiz.q1.label')
 * @returns {string} - Localized string or [KEY_NAME] if not found
 */
export function getString(key) {
  return currentLocaleData.strings[key]
    ? currentLocaleData.strings[key]
    : `[${key.toUpperCase()}]`;
}

/**
 * Check if a string key exists in current locale
 * @param {string} key - String key
 * @returns {boolean} - True if string exists
 */
export function stringExists(key) {
  return currentLocaleData.strings[key] ? true : false;
}

/**
 * Re-initialize locale based on current cookies
 * Call this when cookies change to update the active locale
 */
export function reinitializeLocale() {
  const newLanguage = getLocaleLanguage();
  const newCountry = getLocaleCountry();
  
  console.log(`[Localization] Reinitializing with: language=${newLanguage}, country=${newCountry}`);
  
  // Reset to default locale first
  currentLocaleData = {
    country: localeData.default.country,
    language: localeData.default.language,
    strings: { ...localeData.default.strings },
  };
  currentLocaleKey = "default";
  
  // Check for matching non-default locale
  Object.keys(localeData).forEach((localeKey) => {
    if (localeKey !== "default") {
      const data = localeData[localeKey];
      if (data.country === newCountry && data.language === newLanguage) {
        console.log(`[Localization] ✅ Matched new locale: ${localeKey}`);
        currentLocaleData.country = data.country;
        currentLocaleData.language = data.language;
        Object.assign(currentLocaleData.strings, data.strings);
        currentLocaleKey = localeKey;
      }
    }
  });
  
  return currentLocaleKey;
}

// Export default function for backward compatibility
export default getString;
